// src/components/auth/RequireAdmin.tsx
//
// Route guard: renders children only if the authenticated user has role='admin'.
// Unauthenticated users are redirected to '/'.
// Authenticated non-admins see a 403 page instead of being silently redirected
// (prevents enumeration of admin routes via redirect timing).
//
// Usage in App.tsx:
//   <Route path="/admin/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../AuthProvider';

type Status = 'loading' | 'allowed' | 'forbidden' | 'unauthenticated';

export default function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setStatus('unauthenticated');
      return;
    }

    let cancelled = false;

    const checkRole = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled) return;
        if (error || !data) {
          setStatus('forbidden');
          return;
        }
        setStatus(data.role === 'admin' ? 'allowed' : 'forbidden');
      } catch {
        if (!cancelled) setStatus('forbidden');
      }
    };

    checkRole();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/" replace />;
  }

  if (status === 'forbidden') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-6xl font-bold text-neutral-200">403</p>
        <h1 className="text-2xl font-bold text-neutral-800">Accès refusé</h1>
        <p className="text-neutral-500 max-w-sm">
          Vous n'avez pas les permissions nécessaires pour accéder à cette page.
        </p>
        <a href="/" className="mt-2 text-primary underline text-sm">
          Retour à l'accueil
        </a>
      </div>
    );
  }

  // status === 'allowed'
  return <>{children}</>;
}
